// src/features/services/sales/hooks/useProductionOrders.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchWorkOrdersApi,
  createWorkOrderApi,
  startWorkOrderApi,
  completeWorkOrderApi,
  cancelWorkOrderApi,
  fetchInventoryApi,
  QK_WORK_ORDERS,
  QK_INVENTORY,
  type WorkOrderListResponse,
  type CreateWorkOrderPayload,
  type CreateWorkOrderResponse,
  type CompleteWorkOrderPayload,
  type WorkOrder,
  type SkuInventory,
} from '../api/productionOrderApi';

export function useProductionOrders(params?: { status?: string; limit?: number }) {
  const qc = useQueryClient();

  const list = useQuery<WorkOrderListResponse, Error>({
    queryKey: [...QK_WORK_ORDERS, params],
    queryFn: () => fetchWorkOrdersApi(params),
    staleTime: 30_000,
  });

  const inventory = useQuery<SkuInventory, Error>({
    queryKey: [...QK_INVENTORY],
    queryFn: () => fetchInventoryApi(),
    staleTime: 30_000,
  });

  const create = useMutation<CreateWorkOrderResponse, Error, CreateWorkOrderPayload>({
    mutationFn: (p) => createWorkOrderApi(p),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QK_WORK_ORDERS] }),
  });

  const start = useMutation<WorkOrder, Error, string>({
    mutationFn: (serial) => startWorkOrderApi(serial),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QK_WORK_ORDERS] }),
  });

  const complete = useMutation<WorkOrder, Error, { serial: string; body: CompleteWorkOrderPayload }>({
    mutationFn: ({ serial, body }) => completeWorkOrderApi(serial, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...QK_WORK_ORDERS] });
      qc.invalidateQueries({ queryKey: [...QK_INVENTORY] });
    },
  });

  const cancel = useMutation<WorkOrder, Error, string>({
    mutationFn: (serial) => cancelWorkOrderApi(serial),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QK_WORK_ORDERS] }),
  });

  return { ...list, inventory, create, start, complete, cancel };
}
